import { useCallback, useRef, useState, useEffect } from "preact/hooks";
import { appStore } from "./store.ts";
import { useStore } from "./useStore.ts";

function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const pad = (n: number) => String(n).padStart(2, "0");
  return h > 0 ? `${h}:${pad(m)}:${pad(s)}` : `${m}:${pad(s)}`;
}

/** Self-contained progress bar that interpolates position once per second. */
function ProgressBar() {
  const serverPositionMs = useStore(appStore, (s) => s.positionMs);
  const durationMs = useStore(appStore, (s) => s.durationMs);
  const status = useStore(appStore, (s) => s.status);

  const [displayMs, setDisplayMs] = useState(serverPositionMs);
  const anchorRef = useRef({ positionMs: serverPositionMs, wallTime: Date.now() });

  useEffect(() => {
    anchorRef.current = { positionMs: serverPositionMs, wallTime: Date.now() };
    setDisplayMs(serverPositionMs);
  }, [serverPositionMs]);

  useEffect(() => {
    if (status === "playing") {
      anchorRef.current = { ...anchorRef.current, wallTime: Date.now() };
    }
  }, [status]);

  useEffect(() => {
    if (status !== "playing") return;
    const id = setInterval(() => {
      const { positionMs, wallTime } = anchorRef.current;
      const elapsed = Date.now() - wallTime;
      const interpolated = positionMs + elapsed;
      setDisplayMs(durationMs > 0 ? Math.min(interpolated, durationMs) : interpolated);
    }, 1000);
    return () => clearInterval(id);
  }, [status, durationMs]);

  const progress = durationMs > 0 ? displayMs / durationMs : 0;

  const [hover, setHover] = useState<{ fraction: number; left: number } | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const [drag, setDrag] = useState<{ fraction: number; left: number } | null>(null);
  const dragFractionRef = useRef(0);

  const onSeek = useCallback((positionMs: number) => {
    appStore.getState().send({ type: "command", action: "seek", positionMs });
  }, []);

  useEffect(() => {
    const bar = barRef.current;
    if (!bar || durationMs <= 0) return;

    function getPosition(touch: Touch) {
      const rect = bar!.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (touch.clientX - rect.left) / rect.width));
      return { fraction, left: fraction * rect.width };
    }

    function onTouchStart(e: TouchEvent) {
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const pos = getPosition(touch);
      dragFractionRef.current = pos.fraction;
      setDrag(pos);
    }

    function onTouchMove(e: TouchEvent) {
      e.preventDefault();
      const touch = e.touches[0];
      if (!touch) return;
      const pos = getPosition(touch);
      dragFractionRef.current = pos.fraction;
      setDrag(pos);
    }

    function onTouchEnd() {
      onSeek(Math.round(dragFractionRef.current * durationMs));
      setDrag(null);
    }

    bar.addEventListener("touchstart", onTouchStart, { passive: false });
    bar.addEventListener("touchmove", onTouchMove, { passive: false });
    bar.addEventListener("touchend", onTouchEnd);
    return () => {
      bar.removeEventListener("touchstart", onTouchStart);
      bar.removeEventListener("touchmove", onTouchMove);
      bar.removeEventListener("touchend", onTouchEnd);
    };
  }, [durationMs, onSeek]);

  const handleBarClick = useCallback(
    (e: MouseEvent) => {
      if (durationMs <= 0) return;
      const bar = e.currentTarget as HTMLElement;
      const rect = bar.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      onSeek(Math.round(fraction * durationMs));
    },
    [durationMs, onSeek]
  );

  const handleBarMouseMove = useCallback(
    (e: MouseEvent) => {
      if (durationMs <= 0) return;
      const bar = e.currentTarget as HTMLElement;
      const rect = bar.getBoundingClientRect();
      const fraction = Math.max(0, Math.min(1, (e.clientX - rect.left) / rect.width));
      setHover({ fraction, left: e.clientX - rect.left });
    },
    [durationMs]
  );

  const handleBarMouseLeave = useCallback(() => setHover(null), []);

  return (
    <div style={{ margin: "0 auto 16px", maxWidth: "480px", padding: "0 8px" }}>
      <div
        ref={barRef}
        onClick={handleBarClick}
        onMouseMove={handleBarMouseMove}
        onMouseLeave={handleBarMouseLeave}
        style={{
          position: "relative",
          height: "8px",
          background: "#ddd",
          borderRadius: "4px",
          cursor: durationMs > 0 ? "pointer" : "default",
          touchAction: "none",
        }}
      >
        <div
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            height: "100%",
            width: `${((drag ? drag.fraction : progress) * 100).toFixed(2)}%`,
            background: "#333",
            borderRadius: "4px",
            overflow: "hidden",
          }}
        />
        {(drag ?? hover) && durationMs > 0 && (
          <div
            style={{
              position: "absolute",
              bottom: "14px",
              left: `${(drag ?? hover)!.left}px`,
              transform: "translateX(-50%)",
              background: "#333",
              color: "#fff",
              fontSize: "11px",
              padding: "2px 6px",
              borderRadius: "3px",
              whiteSpace: "nowrap",
              pointerEvents: "none",
            }}
          >
            {formatTime(Math.round((drag ?? hover)!.fraction * durationMs))}
          </div>
        )}
      </div>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          fontSize: "12px",
          color: "#666",
          marginTop: "4px",
        }}
      >
        <span>{formatTime(displayMs)}</span>
        <span>{durationMs > 0 ? formatTime(durationMs) : "--:--"}</span>
      </div>
    </div>
  );
}

function SubtitleControls() {
  const tracks = useStore(appStore, (s) => s.subtitleTracks);
  const activeTrackId = useStore(appStore, (s) => s.activeSubtitleTrackId);
  const delayMs = useStore(appStore, (s) => s.subtitleDelayMs);

  const [localTrackId, setLocalTrackId] = useState(activeTrackId ?? -1);
  const frozenRef = useRef(false);
  const unfreezeTimer = useRef<ReturnType<typeof setTimeout>>();

  const [localDelay, setLocalDelay] = useState(delayMs);
  const [delayCooldown, setDelayCooldown] = useState(false);
  const delayCooldownTimer = useRef<ReturnType<typeof setTimeout>>();

  // Sync from server when select is not frozen
  useEffect(() => {
    if (!frozenRef.current) setLocalTrackId(activeTrackId ?? -1);
  }, [activeTrackId]);

  useEffect(() => {
    if (!delayCooldown) setLocalDelay(delayMs);
  }, [delayMs, delayCooldown]);

  function handleSelectFocus() {
    frozenRef.current = true;
    clearTimeout(unfreezeTimer.current);
  }

  function handleSelectBlur() {
    clearTimeout(unfreezeTimer.current);
    unfreezeTimer.current = setTimeout(() => {
      frozenRef.current = false;
      setLocalTrackId(appStore.getState().activeSubtitleTrackId ?? -1);
    }, 1000);
  }

  function handleSelectTrack(trackId: number) {
    setLocalTrackId(trackId);
    appStore.getState().send({ type: "command", action: "subtitle_track", subtitleTrackId: trackId });
  }

  function handleSetDelay(ms: number) {
    setLocalDelay(ms);
    setDelayCooldown(true);
    appStore.getState().send({ type: "command", action: "subtitle_delay", subtitleDelayMs: ms });
    clearTimeout(delayCooldownTimer.current);
    delayCooldownTimer.current = setTimeout(() => setDelayCooldown(false), 1000);
  }

  const displayDelay = delayCooldown ? localDelay : delayMs;

  return (
    <div style={{ marginTop: "24px", maxWidth: "480px", margin: "24px auto 0", padding: "0 8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
        <label style={{ fontSize: "13px", color: "#666" }}>Subtitles</label>
        <select
          value={localTrackId}
          onFocus={handleSelectFocus}
          onBlur={handleSelectBlur}
          onChange={(e) => handleSelectTrack(Number((e.target as HTMLSelectElement).value))}
          style={{ padding: "6px 8px", fontSize: "13px" }}
        >
          <option value={-1}>Off</option>
          {tracks.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <label style={{ fontSize: "13px", color: "#666" }}>Delay</label>
        <input
          type="number"
          value={displayDelay}
          step={50}
          onChange={(e) => handleSetDelay(Number((e.target as HTMLInputElement).value))}
          style={{ width: "80px", padding: "6px 8px", fontSize: "13px" }}
        />
      </div>
    </div>
  );
}

function AudioControls() {
  const tracks = useStore(appStore, (s) => s.audioTracks);
  const activeTrackId = useStore(appStore, (s) => s.activeAudioTrackId);
  const delayMs = useStore(appStore, (s) => s.audioDelayMs);

  const [localTrackId, setLocalTrackId] = useState(activeTrackId ?? -1);
  const frozenRef = useRef(false);
  const unfreezeTimer = useRef<ReturnType<typeof setTimeout>>();

  const [localDelay, setLocalDelay] = useState(delayMs);
  const [delayCooldown, setDelayCooldown] = useState(false);
  const delayCooldownTimer = useRef<ReturnType<typeof setTimeout>>();

  useEffect(() => {
    if (!frozenRef.current) setLocalTrackId(activeTrackId ?? -1);
  }, [activeTrackId]);

  useEffect(() => {
    if (!delayCooldown) setLocalDelay(delayMs);
  }, [delayMs, delayCooldown]);

  function handleSelectFocus() {
    frozenRef.current = true;
    clearTimeout(unfreezeTimer.current);
  }

  function handleSelectBlur() {
    clearTimeout(unfreezeTimer.current);
    unfreezeTimer.current = setTimeout(() => {
      frozenRef.current = false;
      setLocalTrackId(appStore.getState().activeAudioTrackId ?? -1);
    }, 1000);
  }

  function handleSelectTrack(trackId: number) {
    setLocalTrackId(trackId);
    appStore.getState().send({ type: "command", action: "audio_track", audioTrackId: trackId });
  }

  function handleSetDelay(ms: number) {
    setLocalDelay(ms);
    setDelayCooldown(true);
    appStore.getState().send({ type: "command", action: "audio_delay", audioDelayMs: ms });
    clearTimeout(delayCooldownTimer.current);
    delayCooldownTimer.current = setTimeout(() => setDelayCooldown(false), 1000);
  }

  const displayDelay = delayCooldown ? localDelay : delayMs;

  return (
    <div style={{ marginTop: "12px", maxWidth: "480px", margin: "12px auto 0", padding: "0 8px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", justifyContent: "center" }}>
        <label style={{ fontSize: "13px", color: "#666" }}>Audio</label>
        <select
          value={localTrackId}
          onFocus={handleSelectFocus}
          onBlur={handleSelectBlur}
          onChange={(e) => handleSelectTrack(Number((e.target as HTMLSelectElement).value))}
          style={{ padding: "6px 8px", fontSize: "13px" }}
        >
          <option value={-1}>Default</option>
          {tracks.map((t) => (
            <option key={t.id} value={t.id}>{t.name}</option>
          ))}
        </select>
        <label style={{ fontSize: "13px", color: "#666" }}>Delay</label>
        <input
          type="number"
          value={displayDelay}
          step={50}
          onChange={(e) => handleSetDelay(Number((e.target as HTMLInputElement).value))}
          style={{ width: "80px", padding: "6px 8px", fontSize: "13px" }}
        />
      </div>
    </div>
  );
}

export function PlayerView() {
  const status = useStore(appStore, (s) => s.status);
  const title = useStore(appStore, (s) => s.title);

  const btnStyle = {
    padding: "8px 16px",
    fontSize: "13px",
    cursor: "pointer",
  };

  return (
    <div style={{ textAlign: "center", marginTop: "40px" }}>
      <p
        style={{
          fontSize: "12px",
          textTransform: "uppercase",
          color: "#888",
          marginBottom: "8px",
        }}
      >
        {status}
      </p>
      <h3 style={{ marginBottom: "24px", wordBreak: "break-word" }}>
        {title ? decodeURIComponent(title) : "No media"}
      </h3>

      <ProgressBar />

      <button
        onClick={() => appStore.getState().send({ type: "command", action: "toggle" })}
        style={{
          padding: "16px 48px",
          fontSize: "24px",
          cursor: "pointer",
        }}
      >
        {status === "playing" ? (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><rect x="5" y="3" width="5" height="18"/><rect x="14" y="3" width="5" height="18"/></svg>
        ) : (
          <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor"><polygon points="5,3 21,12 5,21"/></svg>
        )}
      </button>

      <SubtitleControls />
      <AudioControls />

      <div
        style={{
          marginTop: "32px",
          display: "flex",
          flexWrap: "wrap",
          gap: "8px",
          justifyContent: "center",
        }}
      >
        <button onClick={() => appStore.getState().send({ type: "command", action: "toggle_logs" })} style={btnStyle}>Toggle Logs</button>
        <button onClick={() => appStore.getState().send({ type: "command", action: "toggle_status" })} style={btnStyle}>Toggle Status</button>
      </div>
    </div>
  );
}
